import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Search, Filter, ChevronDown, Eye, Edit, X, Package,
  Truck, CheckCircle, XCircle, Clock, RefreshCw, MapPin, Phone, Mail
} from 'lucide-react';
import { adminFetch } from '../../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUSES = [
  { id: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  { id: 'processing', label: 'Processing', color: 'bg-indigo-100 text-indigo-800' },
  { id: 'shipped', label: 'Shipped', color: 'bg-purple-100 text-purple-800' },
  { id: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-800' },
  { id: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' }
];

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: '', tracking_number: '', admin_notes: '' });

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/admin/orders?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await adminFetch(url);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrders();
  };

  const handleStatusUpdate = async () => {
    if (!showStatusModal || !statusUpdate.status) return;
    
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/orders/${showStatusModal.id}/status`, {
        method: 'PUT',
        body: JSON.stringify(statusUpdate)
      });
      
      if (res.ok) {
        toast.success('Order status updated');
        fetchOrders();
        setShowStatusModal(null);
        setStatusUpdate({ status: '', tracking_number: '', admin_notes: '' });
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCancelOrder = async (orderId, reason) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/orders/${orderId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });
      
      if (res.ok) {
        toast.success('Order cancelled');
        fetchOrders();
        setSelectedOrder(null);
      } else {
        toast.error('Failed to cancel order');
      }
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const getStatusColor = (status) => {
    const found = ORDER_STATUSES.find(s => s.id === status);
    return found?.color || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-orders-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-600">Manage customer orders and shipments</p>
        </div>
        <button
          onClick={() => fetchOrders()}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, phone, order ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="order-search-input"
              />
            </div>
          </div>
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              data-testid="order-status-filter"
            >
              <option value="">All Statuses</option>
              {ORDER_STATUSES.map(status => (
                <option key={status.id} value={status.id}>{status.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Search
          </button>
        </form>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No orders found</h3>
            <p className="text-gray-500">Orders will appear here when customers place them</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map(order => (
                  <tr key={order.id} className="hover:bg-gray-50" data-testid={`order-row-${order.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="font-medium text-gray-900">{order.order_number || order.id}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{order.full_name}</p>
                      <p className="text-sm text-gray-500">{order.phone}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">{order.items?.length || 0} items</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="font-semibold text-gray-900">₹{order.total?.toLocaleString()}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        order.payment?.payment_status === 'completed' 
                          ? 'bg-green-100 text-green-800'
                          : order.payment?.payment_method === 'cod'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.payment?.payment_method === 'cod' ? 'COD' : order.payment?.payment_status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelectedOrder(order)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View Details"
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setShowStatusModal(order);
                            setStatusUpdate({ status: order.status, tracking_number: order.tracking_number || '', admin_notes: '' });
                          }}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                          title="Update Status"
                          data-testid={`edit-order-${order.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/billing/invoice/${order.id}`}
                          target="_blank" rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          title="Tax Invoice (PDF print)"
                          data-testid={`invoice-${order.id}`}
                        >
                          📄
                        </a>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/label/${order.id}?token=${localStorage.getItem('admin_token') || ''}`}
                          target="_blank" rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                          title="4x2 inch Shipping Label"
                          data-testid={`label-${order.id}`}
                        >
                          🏷️
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto my-8">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Order Details</h3>
                <p className="text-sm text-gray-500">{selectedOrder.order_number || selectedOrder.id}</p>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-4">
                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedOrder.status)}`}>
                  {selectedOrder.status}
                </span>
                {selectedOrder.tracking_number && (
                  <span className="text-sm text-gray-600">
                    Tracking: <span className="font-medium">{selectedOrder.tracking_number}</span>
                  </span>
                )}
              </div>

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Customer Information</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 font-medium">{selectedOrder.full_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone size={16} />
                    {selectedOrder.phone}
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail size={16} />
                    {selectedOrder.email}
                  </div>
                  <div className="flex items-start gap-2 text-gray-600 md:col-span-2">
                    <MapPin size={16} className="mt-0.5 shrink-0" />
                    <span>{selectedOrder.address}, {selectedOrder.city}, {selectedOrder.state} - {selectedOrder.pincode}</span>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Order Items</h4>
                <div className="border rounded-lg divide-y">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4">
                      <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-contain bg-gray-50" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-500">{item.brand}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">₹{item.price?.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Order Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span>₹{selectedOrder.subtotal?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery</span>
                    <span>{selectedOrder.delivery_charge > 0 ? `₹${selectedOrder.delivery_charge}` : 'Free'}</span>
                  </div>
                  {selectedOrder.discount_amount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount</span>
                      <span>-₹{selectedOrder.discount_amount?.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-base pt-2 border-t">
                    <span>Total</span>
                    <span>₹{selectedOrder.total?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Payment Information</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Method: </span>
                    <span className="font-medium">{selectedOrder.payment?.payment_method === 'cod' ? 'Cash on Delivery' : 'Online Payment'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Status: </span>
                    <span className={`font-medium ${selectedOrder.payment?.payment_status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {selectedOrder.payment?.payment_status || 'Pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Refund Info (if refund exists) */}
              {selectedOrder.refund && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-medium text-orange-900 mb-2">Refund Information</h4>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-600">Status: </span><span className="font-medium capitalize">{selectedOrder.refund.status}</span></div>
                    <div><span className="text-gray-600">Amount: </span><span className="font-medium">{'\u20B9'}{selectedOrder.refund.amount}</span></div>
                    {selectedOrder.refund.reason && <div className="md:col-span-2"><span className="text-gray-600">Reason: </span><span>{selectedOrder.refund.reason}</span></div>}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setSelectedOrder(null);
                    setShowStatusModal(selectedOrder);
                    setStatusUpdate({ status: selectedOrder.status, tracking_number: selectedOrder.tracking_number || '', admin_notes: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Status
                </button>
                {selectedOrder.payment?.payment_method !== 'cod' &&
                 selectedOrder.payment?.razorpay_payment_id &&
                 !selectedOrder.refund?.razorpay_refund_id &&
                 selectedOrder.status !== 'cancelled' && (
                  <button
                    onClick={async () => {
                      const reason = prompt('Enter refund reason:');
                      if (!reason) return;
                      const amount = prompt(`Refund amount (max ${'\u20B9'}${selectedOrder.total}):`, selectedOrder.total);
                      if (!amount) return;
                      try {
                        const res = await adminFetch(`${BACKEND_URL}/api/admin/orders/${selectedOrder.id}/refund`, {
                          method: 'POST',
                          body: JSON.stringify({ reason, amount: parseFloat(amount) })
                        });
                        const data = await res.json();
                        if (res.ok) {
                          toast.success(`Refund of ${'\u20B9'}${data.amount} initiated!`);
                          fetchOrders();
                          setSelectedOrder(null);
                        } else {
                          toast.error(data.detail || 'Refund failed');
                        }
                      } catch { toast.error('Refund request failed'); }
                    }}
                    className="px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 text-sm font-medium"
                    data-testid="refund-btn"
                  >
                    Initiate Refund
                  </button>
                )}
                {selectedOrder.can_cancel && selectedOrder.status !== 'cancelled' && (
                  <button
                    onClick={() => {
                      const reason = prompt('Enter cancellation reason:');
                      if (reason) handleCancelOrder(selectedOrder.id, reason);
                    }}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    Cancel Order
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Update Order Status</h3>
            <p className="text-sm text-gray-500 mb-4">Order: {showStatusModal.order_number || showStatusModal.id}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusUpdate.status}
                  onChange={(e) => setStatusUpdate({...statusUpdate, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="status-update-select"
                >
                  {ORDER_STATUSES.map(status => (
                    <option key={status.id} value={status.id}>{status.label}</option>
                  ))}
                </select>
              </div>
              
              {(statusUpdate.status === 'shipped' || showStatusModal.status === 'shipped') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Number</label>
                  <input
                    type="text"
                    value={statusUpdate.tracking_number}
                    onChange={(e) => setStatusUpdate({...statusUpdate, tracking_number: e.target.value})}
                    placeholder="Enter tracking number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="tracking-number-input"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes (Optional)</label>
                <textarea
                  value={statusUpdate.admin_notes}
                  onChange={(e) => setStatusUpdate({...statusUpdate, admin_notes: e.target.value})}
                  placeholder="Add internal notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowStatusModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                data-testid="confirm-status-update"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;
